type Method = "GET" | "POST" | "PUT" | "DELETE";


export interface RequestConfig {
  method: Method;
  path: string;
  headers?: Record<string, string>;
  params?: Record<string, string>;
  query?: Record<string, string | string[] | undefined>;
  body?: any;
}

export interface Request extends RequestConfig {
  url: URL;
}

export interface Response extends Request {
  statusCode: number;
  body: any;
}

export interface Middleware {
  onRequest(request: Request, next: () => Promise<Response>): Promise<Response>;
}

export enum Order {
  ASC = "ASC",
  DESC = "DESC",
}

export enum FilterCondition {
  And = "&&",
  Or = "||",
}

export enum ComparisonFilterExpression {
  Equal = "=",
  NotEqual = "!=",
  LessThan = "<",
  GreaterThan = ">",
  EqualOrLessThan = "<=",
  EqualOrGreaterThan = ">=",
  Match = "~",
}

export enum StringFilterExpression {
  Equal = "=",
  NotEqual = "!=",
  Match = "~",
  NotMatch = "!~",
}

export enum BooleanFilterExpression {
  Equal = "=",
  NotEqual = "!=",
}

export enum ChoicesFilterExpression {
  In = "=",
  NotIn = "!=",
}

export enum ArrayFilterExpression {
  Contains = "~",
  NotContains = "!~",
}

export enum FilterKind {
  NumericFilter = "NumericFilter",
  DateFilter = "DateFilter",
  StringFilter = "StringFilter",
  BooleanFilter = "BooleanFilter",
  NumericChoicesFilter = "NumericChoicesFilter",
  StringChoicesFilter = "StringChoicesFilter",
  NumericArrayFilter = "NumericArrayFilter",
  StringArrayFilter = "StringArrayFilter",
}

export interface INumericFilter<T> {
  kind: FilterKind.NumericFilter;
  field: keyof T;
  value: number;
  expression: ComparisonFilterExpression;
  filter_condition?: FilterCondition;
}

export interface INumericChoicesFilter<T> {
  kind: FilterKind.NumericChoicesFilter;
  field: keyof T;
  value: number[];
  expression: ChoicesFilterExpression;
  filter_condition?: FilterCondition;
}

export interface IStringFilter<T> {
  kind: FilterKind.StringFilter;
  field: keyof T;
  value: string;
  expression: StringFilterExpression;
  filter_condition?: FilterCondition;
}

export interface IStringChoicesFilter<T> {
  kind: FilterKind.StringChoicesFilter;
  field: keyof T;
  value: string[];
  expression: ChoicesFilterExpression;
  filter_condition?: FilterCondition;
}

export interface IDateFilter<T> {
  kind: FilterKind.DateFilter;
  field: keyof T;
  value: string;
  expression: ComparisonFilterExpression;
  filter_condition?: FilterCondition;
}

export interface IBooleanFilter<T> {
  kind: FilterKind.BooleanFilter;
  field: keyof T;
  value: boolean;
  expression: BooleanFilterExpression;
  filter_condition?: FilterCondition;
}

export interface INumericArrayFilter<T> {
  kind: FilterKind.NumericArrayFilter;
  field: keyof T;
  value: number;
  expression: ArrayFilterExpression;
  filter_condition?: FilterCondition;
}

export interface IStringArrayFilter<T> {
  kind: FilterKind.StringArrayFilter;
  field: keyof T;
  value: string;
  expression: ArrayFilterExpression;
  filter_condition?: FilterCondition;
}

export type IFilter<T> =
  | INumericFilter<T>
  | IStringFilter<T>
  | IDateFilter<T>
  | IBooleanFilter<T>
  | INumericChoicesFilter<T>
  | IStringChoicesFilter<T>
  | INumericArrayFilter<T>
  | IStringArrayFilter<T>;

export interface IOrderBy<T> {
  field: keyof T;
  order: Order;
}

export interface ICollection<T> {
  count: number;
  page: number;
  total_pages: number;
  total_count: number;
  items_per_page: number;
  order_by?: IOrderBy<T>[];
  filter?: IFilter<T>[];
  filter_condition?: FilterCondition;
  items: T[];
}

export interface ICollectionQuery<T> {
  page: number;
  items_per_page: number;
  order_by?: IOrderBy<T>[];
  filter?: IFilter<T>[];
  filter_condition?: FilterCondition;
}

export class JsonParsingError extends Error {
  public readonly body: string;

  constructor(body: string, cause: Error) {
    super(cause.message, {
      cause: cause,
    });
    this.body = body;
  }
}
